import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  AnswerModel,
  CourseModel,
  Exam,
  FlashCardModel,
  QuestionModel,
} from 'src/app/api/models';
import {
  QuestionsService,
  QuizzesService,
  ExamsService,
  CoursesService,
  FlashCardsService,
  LiveEventsService,
  BooksService,
  AiService,
} from 'src/app/api/services';
import { QuestionType, PollQuestionType } from 'src/app/app.types';
import { cloneDeep, sortBy } from 'lodash';
import { Subject, Observable, Subscription, forkJoin } from 'rxjs';
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators';
import { questionTypeDisplayFn } from 'src/app/app.types';
import { AbstractEditComponent } from 'src/app/cms/common/abstract-edit.component';
import { DialogService } from 'src/app/cms/common/dialog.service';
import {
  OnInit,
  OnDestroy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  generatingQuestionForm,
  INFO_DIALOG_WIDTH,
  maxTenNumberValidator,
  validateQuestions,
} from 'src/app/utility/app.utils';
import { ActivatedRoute, Router } from '@angular/router';
import { CancelConfirmationComponent } from '../common/dialogs/cancel-confirmation.component';
import { AuthService } from 'src/app/auth/auth.service';
import { Location, NgTemplateOutlet, NgClass, NgStyle } from '@angular/common';
import { ImageEditDialogComponent } from './image-edit-dialog/image-edit-dialog.component';
import * as SCTemplate from './templates/question/qa-question-exam-single-choice.json';
import * as TFTemplate from './templates/question/qa-question-exam-true-false.json';
import * as MCTemplate from './templates/question/qa-question-exam-multiple-choice.json';
import * as CATemplate from './templates/question/qa-question-exam-cloze.json';
import * as TF3Template from './templates/question/qa-question-exam-free-text-3-points.json';
import * as TF8Template from './templates/question/qa-question-exam-free-text-8-points.json';
import * as SCSurveyTemplate from './templates/polls/single-choice.json';
import * as TFSurveyTemplate from './templates/polls/true-false.json';
import * as MCSurveyTemplate from './templates/polls/multi-choice.json';
import * as SCRSurveyTemplate from './templates/polls/single-choice-rating-scale.json';
import * as FTSurveyTemplate from './templates/polls/free-text.json';
import * as WCSurveyTemplate from './templates/polls/word-cloud.json';

import {
  ShowInfoDialogComponent,
  ShowInfoItem,
} from '../common/show-info-dialog/show-info-dialog.component';
import * as fileSaver from 'file-saver';
import { PanelEntityType } from '../panel/panel.types';
import {
  CommentListDialogComponent,
  CommentListDialogData,
} from '../list/comment-list-dialog.component';
import { MatDialogConfig } from '@angular/material/dialog';
import { CustomTab } from 'src/app/shared/components/custom-tabs/custom-tabs.component';
import { ContentSpinnerComponent } from '../common/ui/content-spinner.component';
import { MatCard } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { NgScrollbar } from 'ngx-scrollbar';
import { CustomTabsComponent } from '../../shared/components/custom-tabs/custom-tabs.component';
import { FormToggleComponent } from '../common/form-field/toggle.component';
import { FormSelectMediaFileComponent } from '../common/form-field/media-input.component';
import { FormInputComponent } from '../common/form-field/input.component';
import { TagInputComponent } from '../common/form-field/tag-input.component';
import { MatIcon } from '@angular/material/icon';
import { QuestionMockPhoneComponent } from './question-mock-phone.component';
import { ImportCsvNavigationComponent } from '../common/import-csv-navigation.component';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormSelectComponent } from '../common/form-field/select.component';
import { ButtonComponent } from '../../shared/components/button/button.component';

@Component({
  selector: 'qa-question-edit',
  templateUrl: './question-edit.component.html',
  styleUrls: ['./question-edit.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ContentSpinnerComponent,
    NgTemplateOutlet,
    MatCard,
    MatButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    NgScrollbar,
    NgClass,
    CustomTabsComponent,
    FormToggleComponent,
    FormSelectMediaFileComponent,
    FormInputComponent,
    TagInputComponent,
    MatIcon,
    QuestionMockPhoneComponent,
    ImportCsvNavigationComponent,
    MatRadioGroup,
    MatRadioButton,
    MatCheckbox,
    NgStyle,
    FormSelectComponent,
    ButtonComponent,
    TranslateModule,
  ],
})
export class QuestionEditComponent
  extends AbstractEditComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() questionId: number;
  @Input() isLiveEvent? = false;
  @Input() isImport = false;
  @Input() importNavigation?: { count: number; index: number };
  private destroy$ = new Subject<void>();
  @Input() importQuestion?: QuestionModel;
  @Output() importQuestionChange = new EventEmitter<QuestionModel>();
  @Output() updatedQuestion = new EventEmitter<QuestionModel>();
  @Output() closeSideMenu = new EventEmitter<boolean>();
  @Output() importNavigate = new EventEmitter<number>();
  loading = true;
  parentExamId: number;
  examPlayId: number;
  parentQuizId: number;
  commentId: number;
  questionTypeEnum;
  generatedWrongAnswerStyleControl = new UntypedFormControl();
  generatedWrongAnswerStyles = [
    {
      name: 'neutral',
      text: this.translator.instant(
        'question.label.generatedWrongAnswerType.neutral'
      ),
      id: 1,
    },
    {
      name: 'short',
      text: this.translator.instant(
        'question.label.generatedWrongAnswerType.short'
      ),
      id: 2,
    },
    {
      name: 'elaborate',
      text: this.translator.instant(
        'question.label.generatedWrongAnswerType.elaborate'
      ),
      id: 3,
    },
    {
      name: 'funny',
      text: this.translator.instant(
        'question.label.generatedWrongAnswerType.funny'
      ),
      id: 4,
    },
  ];
  radioRightAnswerControl = new UntypedFormControl();
  radioValueChanges: Subscription;
  typeValueChanges: Subscription;
  formValueChanges: Subscription;
  courseData: CourseModel;
  question: QuestionModel;
  userId: string;
  courseId: number;
  bookId: number;
  id: number;
  flashCardId: number;
  questionIdFromContent: number;
  duplicateQuestionId: number;
  showForm = false;
  currentItemIndex = 0;
  allQuestions: QuestionModel[];
  liveEventId: number;
  isPollQuestion = false;
  selectedTemplateIndex = -1;
  questionsTypes: CustomTab[] = [];
  templates = [];
  templateType = [];
  hasChange: boolean = false;
  hasComments = false;
  showMobilePreview = false;
  loadAnswers = false;
  showAnswers = true;
  authSub: Subscription;
  suggestion: string;
  QuestionTypeDisplayFn = questionTypeDisplayFn;
  exam: Exam;

  get currentIndex(): number {
    return (
      (this.isImport ? this.importNavigation?.index : this.currentItemIndex) ??
      0
    );
  }

  get totalItems(): number {
    return (
      (this.isImport
        ? this.importNavigation?.count
        : this.allQuestions?.length) ?? 0
    );
  }

  get hasAtLeastOneAnswer(): boolean {
    return this.form
      .get('answers')
      .value.some((answer: AnswerModel) => !!answer.text);
  }

  constructor(
    private dialog: DialogService,
    private exams: ExamsService,
    private matDialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private flashCards: FlashCardsService,
    private questions: QuestionsService,
    private quizzes: QuizzesService,
    private liveEventService: LiveEventsService,
    private ai: AiService,
    private route: ActivatedRoute,
    private location: Location,
    private router: Router,
    auth: AuthService,
    private coursesService: CoursesService,
    private booksService: BooksService,
    private translator: TranslateService
  ) {
    super();
    this.authSub = auth.currentUserInfo$.subscribe((value) => {
      if (value) {
        this.userId = value.sub;
      }
    });
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      if (params.isPoll) {
        this.isPollQuestion = true;
      }
      if (params.course || params.courseId) {
        this.courseId = params.course || params.courseId;
      }
      if (this.route?.snapshot?.params?.course) {
        this.courseId = this.route.snapshot.params.course;
      }
      if (params.book) {
        this.bookId = params.book;
      }
      if (params.commentId) {
        this.commentId = params.commentId;
      }
      if (this.route.snapshot.params.quiz) {
        this.parentQuizId = this.route.snapshot.params.quiz;
      }
      if (params.quiz) {
        this.parentQuizId = params.quiz;
      }
      if (params.exam) {
        this.parentExamId = params.exam;
      }
      if (this.route.snapshot.params.examId) {
        this.examPlayId = this.route.snapshot.params.examId;
      }
      if (params.liveEventId) {
        this.liveEventId = params.liveEventId;
      }
      if (params.events) {
        this.liveEventId = params.events;
      }
      if (params.flashCardId) {
        this.flashCardId = Number(params.flashCardId);
        if (!this.isImport) this.getFlashCard(this.flashCardId);
      }
      if (params.questionId) {
        this.duplicateQuestionId = Number(params.questionId);
        if (!this.isImport) this.getAllQuestions(this.duplicateQuestionId);
      }
      if (params.contentQuestionId && !(params.course || params.courseId)) {
        if (localStorage.getItem('content-question-list')) {
          this.questionIdFromContent = Number(params.contentQuestionId);
          const questions = localStorage.getItem('content-question-list');
          this.allQuestions = JSON.parse(questions).data;
          const idx = this.allQuestions.findIndex(
            (question) => question.id === Number(params.contentQuestionId)
          );
          this.currentItemIndex = idx > -1 ? idx : 0;
        }
      }
    });
    this.route.params.subscribe((params) => {
      if (params.id) {
        this.id = params.id;
        if (!this.isImport) this.getAllQuestions(params.id);
      } else if (!this.flashCardId && !this.duplicateQuestionId) {
        this.loading = false;
      }
      if (params.quiz) {
        this.parentQuizId = params.quiz;
      }
      if (params.examId) {
        this.parentExamId = params.examId;
      }
      if (params.liveEventId) {
        this.liveEventId = params.liveEventId;
      }
    });
    this.setQuestionEnums();
    if (!this.isImport) {
      this.getQuestionInfo();
    }
    this.generatedWrongAnswerStyleControl.setValue(
      this.generatedWrongAnswerStyles[0]
    );
    this.initForm();
  }

  initForm() {
    if (this.form) {
      return;
    }
    this.form = new UntypedFormGroup({
      id: new UntypedFormControl(),
      text: new UntypedFormControl(),
      type: new UntypedFormControl(),
      max_score: new UntypedFormControl(1),
      max_answer_submissions: new UntypedFormControl(1),
      max_num_characters: new UntypedFormControl(),
      is_right: new UntypedFormControl(),
      answers: new UntypedFormArray([]),
      weblink: new UntypedFormControl(),
      source: new UntypedFormControl(),
      tags: new UntypedFormControl([]),
      explanation: new UntypedFormControl(),
      display_in_order: new UntypedFormControl(),
      media: new UntypedFormGroup({
        id: new UntypedFormControl(),
        file_ext: new UntypedFormControl(),
        uuid: new UntypedFormControl(),
      }),
      sample_answer: new UntypedFormControl(),
    });

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isImport) {
        this.importQuestionChange.emit(this.form.getRawValue());
      }
    });
    if (this.isImport) {
      this.id = undefined;
      this.duplicateQuestionId = undefined;
      this.flashCardId = undefined;
      this.loading = false;
    }
    if (this.importQuestion) {
      this.form.patchValue(this.importQuestion, {
        emitEvent: false,
      });
    }
    if (!this.id && !this.flashCardId && !this.duplicateQuestionId) {
      if (!this.isImport) {
        generatingQuestionForm(
          { answers: [] },
          this.form,
          this.radioRightAnswerControl,
          this.radioValueChanges,
          this.typeValueChanges
        );
        if (this.isPollQuestion) {
          this.form.patchValue({ display_in_order: true });
        }
        if (this.templateType.length > 0) {
          this.selectTemplate(this.templateType[0], 0);
        }
      } else {
        const question = this.importQuestion ?? this.form.getRawValue();
        generatingQuestionForm(
          question,
          this.form,
          this.radioRightAnswerControl,
          this.radioValueChanges,
          this.typeValueChanges
        );
        this.setSelectedTemplateIndex();
      }
      this.generatedWrongAnswerStyleControl.disable();
      this.suggestion = this.explanationSuggestion();
      this.showForm = true;
    }

    (this.form.get('answers') as UntypedFormArray).valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.suggestion = this.explanationSuggestion();
      });

    this.form
      .get('type')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value !== null) {
          this.suggestion = this.explanationSuggestion();
          if (value === QuestionType.FREETEXT) {
            this.setQuestionEnums();
            if (!this.isPollQuestion) {
              this.form.get('max_score').setValidators([Validators.required]);
              this.form.get('max_score').valueChanges.subscribe((value) => {
                if (value && value < 1) {
                  this.form.get('max_score').setValue(1);
                }
              });
            } else {
              this.form
                .get('max_answer_submissions')
                .setValidators([Validators.required, maxTenNumberValidator]);
              this.form
                .get('max_answer_submissions')
                .valueChanges.subscribe((value) => {
                  if (!Number.isNaN(parseInt(value)) && value < 1) {
                    this.form.get('max_answer_submissions').setValue(1);
                  }
                });
            }
          } else {
            this.form.get('max_answer_submissions').reset();
            this.form.get('max_answer_submissions').clearValidators();
            this.form.get('max_score').clearValidators();
            if (value === QuestionType.SINGLE_CHOICE) {
              const formArrayAnswers = this.form.get(
                'answers'
              ) as UntypedFormArray;
              formArrayAnswers.controls.forEach((control, index) => {
                control.patchValue({ is_right: index === 0 ? true : false });
              });
              this.form.get('answers').updateValueAndValidity();
            }
          }
          this.form.get('max_score').updateValueAndValidity();
          this.form.get('max_answer_submissions').updateValueAndValidity();
        }
      });

    this.form
      .get('text')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!value) {
          this.generatedWrongAnswerStyleControl.disable();
        } else {
          this.generatedWrongAnswerStyleControl.enable();
        }
      });
    if (!this.questionId) {
      this.toggleSidebar(false);
    }
    if (this.id && !this.isImport) {
      this.getComments();
    }
  }

  isAllAnswerUnchecked(): boolean {
    if (this.form?.value.type !== this.questionTypeEnum.MULTIPLE_CHOICE) {
      return false;
    }
    const controls = this.form.get('answers')['controls'];
    const isChecked = controls.find((control) => control?.value?.is_right);
    return !!!isChecked;
  }

  getComments() {
    this.questions
      .getCommentsForQuestion({
        id: this.id,
      })
      .subscribe((response) => {
        this.hasComments = !!response.length;
      });
  }

  showComments() {
    const id = this.question?.id || this.questionId;
    this.matDialog.open(CommentListDialogComponent, {
      data: {
        questionId: id,
      },
      autoFocus: false,
    } as MatDialogConfig<CommentListDialogData>);
  }

  openDuplicateSuccessfulDialog() {
    const buttons = [
      {
        color: 'primary',
        text: this.translator.instant(
          this.parentExamId
            ? 'question.backToExam'
            : this.parentQuizId
            ? 'question.backToQuiz'
            : 'question.backToEvent'
        ),
        value: false,
      },
      {
        color: 'primary',
        text: this.translator.instant('flashCard.goToQuestionOverview'),
        value: true,
      },
    ];
    if (!this.parentExamId && !this.parentQuizId && !this.liveEventId) {
      buttons.splice(0, 1);
    } else {
      buttons.splice(1, 1);
    }
    this.dialog
      .confirm(
        {
          title: this.translator.instant('question.duplicatedTitle'),
          text: this.translator.instant('question.duplicatedMessage'),
          buttons,
        },
        {
          autoFocus: false,
          disableClose: true,
        }
      )
      .subscribe((val) => {
        if (val) {
          this.router.navigate(['/cms/content/questions']);
        } else if (val === false) {
          if (this.courseId) {
            this.redirectBasedOnParent('courses', 'course', this.courseId);
          } else if (this.bookId) {
            this.redirectBasedOnParent('books', 'book', this.bookId);
          } else {
            this.onClose();
          }
        }
      });
  }

  onValueChange(e) {
    setTimeout(() => {
      this.showMobilePreview = e;
    }, 100);
  }

  changeRightAnswer() {
    this.suggestion = this.explanationSuggestion();
  }

  onCreateGroupFormValueChange() {
    const initialValue = this.form.value;
    this.formValueChanges = this.form.valueChanges.subscribe(() => {
      this.hasChange = Object.keys(initialValue).some(
        (key) => this.form.value[key] != initialValue[key]
      );
    });
  }

  exportAsJson() {
    let fullItem: QuestionModel;
    fullItem = cloneDeep(this.question);
    delete fullItem.id;
    fullItem['meta:importType'] = PanelEntityType.Question;
    const file = `${JSON.stringify(fullItem)}\n`;
    const blob = new Blob([file], { type: 'application/json' });
    fileSaver.saveAs(
      blob,
      `qa-${PanelEntityType.Question}-${fullItem.text}.json`
    );
  }

  openConvertedQuestionDialog() {
    this.dialog
      .confirm(
        {
          title: this.translator.instant('question.convertedTitle'),
          text: this.translator.instant('question.convertedMessage'),
          buttons: [
            {
              color: 'primary',
              text: this.translator.instant('flashCard.goToQuestionOverview'),
              value: true,
            },
          ],
        },
        {
          autoFocus: false,
          disableClose: true,
        }
      )
      .subscribe(() => {
        this.router.navigate(['/cms/content/questions']);
      });
  }

  confirmSave(changes) {
    this.dialog
      .confirm({
        title: '',
        text: this.translator.instant('common.message.confirmEdit'),
        buttons: [
          { text: this.translator.instant('common.button.abort') },
          {
            color: 'primary',
            text: this.translator.instant('common.button.save'),
            value: true,
          },
        ],
      })
      .subscribe((val) => {
        if (!val) {
          this.loadQuestion(changes);
        } else {
          this.hasChange = false;
          this.onSubmit();
        }
      });
  }

  toggleSidebar(show: boolean) {
    const sidebar = document.getElementsByClassName('qa-sidebar-inner');
    if (sidebar && sidebar.length) {
      const sidenav = sidebar[0] as HTMLElement;
      sidenav.style.display = show ? 'flex' : 'none';
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes?.questionId?.currentValue) {
      if (this.hasChange) {
        await this.confirmSave(changes);
      } else {
        this.loadQuestion(changes);
      }
    }
  }

  loadQuestion(changes) {
    this.showForm = false;
    this.showMobilePreview = false;
    if (this.formValueChanges) {
      this.formValueChanges.unsubscribe();
    }
    this.questionId = changes.questionId.currentValue;
    this.id = changes.questionId.currentValue;
    this.getAllQuestions(this.id);
  }

  setQuestionEnums() {
    const params = this.route.snapshot.queryParams;
    this.questionTypeEnum = this.isPollQuestion
      ? PollQuestionType
      : QuestionType;
    const disableSurvey = !(
      this.liveEventId ||
      this.examPlayId ||
      this.parentExamId ||
      (this.isImport && params.type !== 'question')
    );
    this.questionsTypes = [
      {
        name: this.translator.instant('question.label.quizQuestionShort'),
        value: 0,
        icon: 'chrome_reader_mode',
      },
      {
        name: this.translator.instant('question.label.surveyQuestionShort'),
        value: 1,
        isDisabled: disableSurvey,
        tooltip: this.translator.instant(
          'question.label.surveyQuestionDisabledTooltip'
        ),
        icon: 'insert_chart_outlined',
      },
    ];
    this.setTemplates();
    this.setTemplateType(this.isPollQuestion);
  }

  setTemplates() {
    const language = this.translator.currentLang;
    const pollTemplates = [
      {
        title: this.translator.instant('questionTemplate.title.multiChoice'),
        description: this.translator.instant(
          'questionTemplate.description.multipleAnswersPossible'
        ),
        icon: 'check_box',
        type: PollQuestionType.MULTIPLE_CHOICE,
        template: MCSurveyTemplate[language],
      },
      {
        title: this.translator.instant('questionTemplate.title.yesNo'),
        description: this.translator.instant(
          'questionTemplate.description.yesNo'
        ),
        icon: 'thumbs_up_down',
        type: PollQuestionType.SINGLE_CHOICE,
        template: TFSurveyTemplate[language],
      },
      {
        title: this.translator.instant(
          'questionTemplate.title.satisfactionScale'
        ),
        description: this.translator.instant(
          'questionTemplate.description.satisfactionScale'
        ),
        icon: 'star_rate',
        type: PollQuestionType.SINGLE_CHOICE,
        template: SCSurveyTemplate[language],
      },
      {
        title: this.translator.instant('questionTemplate.title.ratingScale'),
        description: this.translator.instant(
          'questionTemplate.description.ratingScale'
        ),
        icon: 'linear_scale',
        type: PollQuestionType.SINGLE_CHOICE,
        template: SCRSurveyTemplate[language],
      },
      {
        title: this.translator.instant('questionTemplate.title.wordCloud'),
        description: this.translator.instant(
          'questionTemplate.description.wordCloud'
        ),
        icon: 'cloud',
        type: PollQuestionType.FREETEXT,
        template: WCSurveyTemplate[language],
      },
      {
        title: this.translator.instant('questionTemplate.title.openFeedback'),
        description: this.translator.instant(
          'questionTemplate.description.openFeedback'
        ),
        icon: 'forum',
        type: PollQuestionType.FREETEXT,
        template: FTSurveyTemplate[language],
      },
    ];

    const quizTemplates = [
      {
        title: this.translator.instant('questionTemplate.title.singleChoice'),
        description: this.translator.instant(
          'questionTemplate.description.singleChoice'
        ),
        icon: 'radio_button_checked',
        type: QuestionType.SINGLE_CHOICE,
        template: SCTemplate[language],
      },
      {
        title: this.translator.instant('questionTemplate.title.multiChoice'),
        description: this.translator.instant(
          'questionTemplate.description.multiChoice'
        ),
        icon: 'check_box',
        type: QuestionType.MULTIPLE_CHOICE,
        template: MCTemplate[language],
      },
      {
        title: this.translator.instant('questionTemplate.title.trueFalse'),
        description: this.translator.instant(
          'questionTemplate.description.trueFalse'
        ),
        icon: 'rule',
        type: QuestionType.TRUE_FALSE,
        template: TFTemplate[language],
      },
      {
        title: this.translator.instant('questionTemplate.title.fillBlank'),
        description: this.translator.instant(
          'questionTemplate.description.fillBlank'
        ),
        icon: 'text_fields',
        type: QuestionType.SINGLE_CHOICE,
        template: CATemplate[language],
      },
    ];
    if (this.parentExamId) {
      quizTemplates.push(
        ...[
          {
            title: this.translator.instant(
              'questionTemplate.title.openQuestion'
            ),
            description: this.translator.instant(
              'questionTemplate.description.openQuestion'
            ),
            icon: 'edit_note',
            type: QuestionType.FREETEXT,
            template: TF3Template[language],
          },
          {
            title: this.translator.instant('questionTemplate.title.matching'),
            description: this.translator.instant(
              'questionTemplate.description.matching'
            ),
            icon: 'device_hub',
            type: QuestionType.FREETEXT,
            template: TF8Template[language],
          },
        ]
      );
    }
    this.templates = this.isPollQuestion ? pollTemplates : quizTemplates;
  }

  setTemplateType(isPollQuestion = false) {
    const params = this.route.snapshot.queryParams;
    const isExam = this.parentExamId || params.type === 'exam-question';
    const disabledFreeText = !(isExam || isPollQuestion);
    const templateType = [
      {
        title: this.translator.instant('questionTemplate.title.singleChoice'),
        description: this.translator.instant(
          isPollQuestion
            ? 'questionTemplate.description.chooseOneAnswer'
            : 'questionTemplate.description.singleChoice'
        ),
        icon: 'radio_button_checked',
        type: QuestionType.SINGLE_CHOICE,
        isPoll: isPollQuestion,
      },
      {
        title: this.translator.instant('questionTemplate.title.multiChoice'),
        description: this.translator.instant(
          isPollQuestion
            ? 'questionTemplate.description.multipleAnswersPossible'
            : 'questionTemplate.description.multiChoice'
        ),
        icon: 'check_box',
        type: QuestionType.MULTIPLE_CHOICE,
        isPoll: isPollQuestion,
      },
      {
        title: this.translator.instant('questionTemplate.title.trueFalse'),
        description: this.translator.instant(
          'questionTemplate.description.trueFalse'
        ),
        icon: 'rule',
        type: QuestionType.TRUE_FALSE,
        isPoll: isPollQuestion,
        isDisabled: isPollQuestion,
        tooltip: this.translator.instant('questionTemplate.tooltip.trueFalse'),
      },
      {
        title: this.translator.instant('questionTemplate.title.openQuestion'),
        description: this.translator.instant(
          'questionTemplate.description.openQuestion'
        ),
        icon: 'edit_note',
        type: QuestionType.FREETEXT,
        isPoll: isPollQuestion,
        isDisabled: disabledFreeText,
        tooltip: this.translator.instant(
          'questionTemplate.tooltip.openQuestion'
        ),
      },
    ];

    this.templateType = templateType;
  }

  duplicatedQuestion() {
    if (this.question) {
      const obj = { questionId: this.question.id.toString() };
      if (this.isPollQuestion) {
        obj['isPoll'] = true;
      }
      if (this.courseId) {
        obj['course'] = this.courseId;
      }
      if (this.parentQuizId) {
        obj['quiz'] = this.parentQuizId;
      }
      if (this.parentExamId) {
        obj['exam'] = this.parentExamId;
      }
      if (this.liveEventId) {
        obj['liveEventId'] = this.liveEventId;
      }
      this.router.navigate(['/cms/add/question'], { queryParams: obj });
    }
  }

  getFlashCard(id) {
    this.flashCards
      .getFlashCardById({
        id,
      })
      .subscribe((flashCard: FlashCardModel) => {
        this.convertQuestion(flashCard);
      });
  }

  convertToFlashcard() {
    this.router.navigate(['/cms/add/flash-card'], {
      queryParams: { questionId: this.question.id.toString() },
    });
  }

  convertQuestion(card: FlashCardModel) {
    const question: QuestionModel = {
      text: card.text,
      media: card.media ? card.media : null,
      tags: card.tags ? card.tags : null,
      explanation: card.explanation ? card.explanation : null,
      weblink: card.weblink ? card.weblink : null,
      source: card.source ? card.source : null,
      answers: [],
      type: null,
    };
    for (const propName in question) {
      if (question[propName] === null || question[propName] === undefined) {
        delete question[propName];
      }
    }
    if (card.answer) {
      const answers = card.answer.split(/\r?\*/);
      if (answers.length > 1 && card.answer.charAt(0) === '*') {
        question.type = QuestionType.MULTIPLE_CHOICE;
        answers.forEach((answer) => {
          if (answer) {
            question.answers.push({
              text: answer.trim(),
              is_right: true,
              question_id: null,
              num_selected: null,
            });
          }
        });
        generatingQuestionForm(
          question,
          this.form,
          this.radioRightAnswerControl,
          this.radioValueChanges,
          this.typeValueChanges
        );
        setTimeout(() => {
          this.showForm = true;
          this.loading = false;
          this.form.markAsDirty();
        }, 200);
      } else {
        question.type = QuestionType.SINGLE_CHOICE;
        question.answers = [
          {
            text: card.answer,
            is_right: true,
            question_id: null,
            num_selected: null,
          },
          {
            text: this.translator.instant('question.label.wrongAnswer'),
            is_right: false,
            question_id: null,
            num_selected: null,
          },
        ];

        generatingQuestionForm(
          question,
          this.form,
          this.radioRightAnswerControl,
          this.radioValueChanges,
          this.typeValueChanges
        );
        setTimeout(() => {
          this.showForm = true;
          this.loading = false;
          this.form.markAsDirty();
        }, 200);
      }
      this.setSelectedTemplateIndex();
    }
  }

  getAllQuestions(questionId: number) {
    if (this.isImport) {
      return;
    }
    if (this.parentQuizId || this.parentExamId) {
      this.loading = true;
      let getQuestions$: Observable<QuestionModel[]>;
      if (this.parentQuizId) {
        getQuestions$ = this.quizzes.getQuestionsForQuiz({
          id: this.parentQuizId,
        });
      } else if (this.parentExamId) {
        getQuestions$ = this.exams.getQuestionsForExam({
          id: this.parentExamId,
        });
      }
      getQuestions$.subscribe({
        next: (response) => {
          this.allQuestions = response;
          const idx = this.allQuestions.findIndex(
            (question) => question.id === Number(questionId)
          );
          this.currentItemIndex = idx > -1 ? idx : 0;
          this.questions
            .getQuestionById({ id: questionId })
            .subscribe((question: QuestionModel) => {
              if (this.duplicateQuestionId) {
                delete question.id;
              }
              this.question = question;
              this.isPollQuestion = question.is_poll_question;
              this.setQuestionEnums();
              generatingQuestionForm(
                this.question,
                this.form,
                this.radioRightAnswerControl,
                this.radioValueChanges,
                this.typeValueChanges
              );
              if (question.type === QuestionType.FREETEXT) {
                if (question.answers?.length) {
                  this.form
                    .get('sample_answer')
                    .setValue(question.answers[0].text);
                }
              }
              this.setSelectedTemplateIndex();
              setTimeout(() => {
                this.showForm = true;
                this.loading = false;
                this.hasChange = false;
                if (this.duplicateQuestionId) {
                  this.form.markAsDirty();
                }
                this.onCreateGroupFormValueChange();
              }, 200);
            });
        },
        error: () => (this.loading = false),
      });
    } else {
      this.loading = true;
      this.questions.getQuestionById({ id: questionId }).subscribe(
        (question: QuestionModel) => {
          if (this.duplicateQuestionId) {
            delete question.id;
          }
          this.question = question;
          this.isPollQuestion = question.is_poll_question;
          this.setQuestionEnums();
          generatingQuestionForm(
            this.question,
            this.form,
            this.radioRightAnswerControl,
            this.radioValueChanges,
            this.typeValueChanges
          );
          if (question.type === QuestionType.FREETEXT) {
            if (question.answers?.length) {
              this.form.get('sample_answer').setValue(question.answers[0].text);
            }
          }
          this.setSelectedTemplateIndex();
          setTimeout(() => {
            this.showForm = true;
            this.loading = false;
            if (this.duplicateQuestionId) {
              this.form.markAsDirty();
            }
          }, 100);
        },
        () => (this.loading = false)
      );
    }
  }

  get getSingleChoiceExplanation() {
    const answer = this.form?.value?.answers.find(
      (answer) => answer?.is_right === true
    );
    return this.translator.instant('question.suggestions.singleChoice', {
      answer: answer?.text ?? '',
    });
  }

  get getTrueFalseExplanation() {
    const isRight = this.form.get('is_right').value;
    const text = this.form.get('text').value;
    return isRight
      ? this.translator.instant('question.suggestions.trueFalse.right', {
          text,
        })
      : this.translator.instant('question.suggestions.trueFalse.wrong');
  }

  get getMultiChoiceExplanation() {
    let correctAnswers = this.form
      .get('answers')
      .value.filter((val) => val.is_right === true)
      .map((ans) => ans?.text ?? '');
    correctAnswers = correctAnswers.map((ans) => `* ${ans}`).join('\n');
    return this.translator.instant('question.suggestions.multiChoice', {
      correctAnswers: correctAnswers ?? '',
    });
  }

  getQuestionInfo() {
    if (this.isImport) {
      return;
    }
    const requestArray = [];
    if (this.courseId) {
      requestArray.push(
        this.coursesService.getCourseById({ id: this.courseId, full: false })
      );
    }
    if (this.bookId) {
      requestArray.push(
        this.booksService.getBookById({ id: this.bookId, full: false })
      );
    }
    if (this.parentExamId && !isNaN(this.parentExamId)) {
      requestArray.push(this.exams.getExamById({ id: this.parentExamId }));
    }
    if (this.parentQuizId) {
      requestArray.push(this.quizzes.getQuizById({ id: this.parentQuizId }));
    }
    forkJoin(requestArray).subscribe((results) => {
      if (results && results.length) {
        const course = results.find((result) => result.type === 'course');
        if (course) {
          this.courseData = course;
        }
        if (this.parentExamId) {
          this.exam = results[0];
        }
      }
    });
  }

  ngOnDestroy() {
    this.toggleSidebar(true);
    if (this.typeValueChanges) this.typeValueChanges.unsubscribe();
    if (this.radioValueChanges) this.radioValueChanges.unsubscribe();
    if (this.authSub) {
      this.authSub.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onAddAnswer(): void {
    const answer = new UntypedFormGroup({
      is_right: new UntypedFormControl(),
      text: new UntypedFormControl(null, [Validators.required]),
    });
    (this.form.get('answers') as UntypedFormArray).push(answer);
    this.form.get('answers').markAsDirty();
    this.form.get('answers').markAsTouched();
  }

  displayWrongAnswerTypeOption(input: { name: string; text: string }): string {
    return input && input.text;
  }

  onGeneratedWrongAnswerStyleChanged(style: { name: any }) {
    this.generatedWrongAnswerStyleControl.setValue(style);
  }

  onGenerateWrongAnswers(): void {
    this.loadAnswers = true;
    const answers = this.form.getRawValue().answers;
    this.ai
      .generateAnswers({
        body: {
          answer_type: this.generatedWrongAnswerStyleControl.value.name,
          num_answers: answers.filter((answer: AnswerModel) => !answer.is_right)
            .length,
          correct_answers: answers
            .filter((answer: AnswerModel) => answer.is_right && answer.text)
            .map((answer: AnswerModel) => answer.text),
          question: this.form.get('text').value,
        },
      })
      .subscribe({
        next: (response) => {
          (this.form.get('answers') as UntypedFormArray).controls
            .filter((control) => {
              return !control.get('is_right').value;
            })
            .forEach((control, i) => {
              control.patchValue({ text: response[i]?.text });
            });
          this.form.get('answers').markAsDirty();
          this.form.get('answers').markAsTouched();
          this.form.get('answers').updateValueAndValidity();
          this.loadAnswers = false;
          this.showAnswers = false;
          setTimeout(() => {
            this.showAnswers = true;
          }, 0);
        },
        error: (error) => {
          this.loadAnswers = false;
          if (error.status === 422) {
            this.matSnackBar.open(
              this.translator.instant(
                'question.message.generateWrongAnswersMoreInputNeeded'
              )
            );
          } else {
            this.matSnackBar.open(
              this.translator.instant(
                'question.message.generateWrongAnswersFailed'
              )
            );
          }
        },
      });
  }

  onRemoveAnswer(index: number): void {
    const length = this.form.get('answers')['controls'].length;
    if (length <= 2) {
      this.matSnackBar.open(
        this.translator.instant('content.question.deleteItem.minimumAnswer')
      );
      return;
    }
    (this.form.get('answers') as UntypedFormArray).removeAt(index);
    this.form.get('answers').markAsDirty();
    this.form.get('answers').markAsTouched();
  }

  onDelete(): void {
    this.dialog
      .confirmDelete()
      .pipe(filter((isConfirmed) => isConfirmed))
      .subscribe(() => {
        this.loading = true;
        const questionId = this.form.value.id as number;
        this.questions.deleteQuestion({ id: questionId }).subscribe({
          next: () => {
            this.form.markAsUntouched();
            this.matSnackBar.open(
              this.translator.instant('content.question.deleteItem.success')
            );
            this.onClose(true);
          },
          error: () => {
            this.loading = false;
            this.matSnackBar.open(
              this.translator.instant('content.question.deleteItem.error')
            );
          },
        });
      });
  }

  checkForSampleAnswer() {
    this.matDialog
      .open<ShowInfoDialogComponent, ShowInfoItem>(ShowInfoDialogComponent, {
        data: {
          infoData: this.translator.instant('question.form.sampleAnswer'),
          closeButton: 'question.form.tagHint.understood',
        },
        width: INFO_DIALOG_WIDTH,
        height: 'auto',
        autoFocus: false,
        panelClass: 'tooltip-modal',
      })
      .afterClosed()
      .subscribe((r) => {
        if (r) {
          localStorage.setItem('sample-answer-log', 'true');
        }
      });
  }

  onSubmit(createAnother = false, makeDuplicate?: boolean): void {
    const showDialog = localStorage.getItem('sample-answer-log');
    if (
      !showDialog &&
      this.form.get('type').value === QuestionType.FREETEXT &&
      !this.form.get('sample_answer').value
    ) {
      this.checkForSampleAnswer();
      return;
    }
    this.loading = true;
    const newQuestion: QuestionModel = this.form.getRawValue();
    if (newQuestion.media && !newQuestion.media.id) {
      newQuestion.media = null;
    }
    if (newQuestion.type === QuestionType.TRUE_FALSE) {
      delete newQuestion.answers;
      delete newQuestion.max_score;
      newQuestion.is_right = !!newQuestion.is_right;
    } else if (newQuestion.type === QuestionType.FREETEXT) {
      delete newQuestion.answers;
      delete newQuestion.is_right;
      if (!this.isPollQuestion) {
        if (this.form.get('sample_answer').value) {
          newQuestion.answers = [
            {
              text: this.form.get('sample_answer').value,
              is_right: true,
            } as AnswerModel,
          ];
        }
      }
      newQuestion.max_score = Number(newQuestion.max_score);
      if (newQuestion.max_num_characters !== null) {
        newQuestion.max_num_characters = Number(newQuestion.max_num_characters);
      }
      if (
        newQuestion.max_answer_submissions &&
        Number(newQuestion.max_answer_submissions)
      ) {
        newQuestion.max_answer_submissions = Number(
          newQuestion.max_answer_submissions
        );
      } else {
        newQuestion.max_answer_submissions = null;
      }
      delete newQuestion['sample_answer'];
    } else {
      delete newQuestion.max_score;
      newQuestion.answers = newQuestion.answers.map((answer) => {
        answer.is_right = Boolean(answer.is_right);
        return answer;
      });
    }
    let saveQuestion$: Observable<QuestionModel>;
    const sentryInfo = {
      title: `Error while create/update question`,
      file: 'question-edit.component.ts',
      api: '',
    };
    if (newQuestion.id && this.userId === this.question.created_by) {
      newQuestion.is_poll_question = !!this.isPollQuestion;
      sentryInfo.api = 'updateQuestion';
      saveQuestion$ = this.questions.updateQuestion({
        id: newQuestion.id,
        body: newQuestion,
      });
    } else if (
      this.parentExamId &&
      !(this.courseData && this.courseData.ref_course_id && newQuestion.id)
    ) {
      newQuestion.is_poll_question = !!this.isPollQuestion;
      delete newQuestion.id;
      sentryInfo.api = 'copyQuestionToExam';
      saveQuestion$ = this.exams
        .copyQuestionToExam({
          id: this.parentExamId,
          body: [newQuestion],
        })
        .pipe(
          map((questions) => {
            sortBy(questions, 'id');
            return questions[questions.length - 1];
          })
        );
    } else if (
      this.parentQuizId &&
      !(this.courseData && this.courseData.ref_course_id && newQuestion.id)
    ) {
      delete newQuestion.id;
      sentryInfo.api = 'copyQuestionToQuiz';
      saveQuestion$ = this.quizzes
        .copyQuestionToQuiz({
          id: this.parentQuizId,
          body: [newQuestion],
        })
        .pipe(
          map((questions) => {
            sortBy(questions, 'id');
            return questions[questions.length - 1];
          })
        );
    } else if (this.liveEventId) {
      newQuestion.is_poll_question = !!this.isPollQuestion;
      sentryInfo.api = 'copyQuestionToEvent';
      saveQuestion$ = this.liveEventService
        .copyQuestionToEvent({ id: this.liveEventId, body: [newQuestion] })
        .pipe(
          map((questions) => {
            sortBy(questions, 'id');
            return questions[questions.length - 1];
          })
        );
    } else {
      newQuestion.is_poll_question = !!this.isPollQuestion;
      sentryInfo.api = 'createQuestion';
      saveQuestion$ = this.questions.createQuestion({ body: newQuestion });
    }
    const { isValid } = validateQuestions(
      sentryInfo,
      [newQuestion],
      this.translator,
      this.matSnackBar
    );
    if (!isValid) {
      this.loading = false;
      this.hasChange = false;
      return;
    }
    saveQuestion$.subscribe({
      next: (question) => {
        this.hasChange = false;
        this.form.markAsUntouched();
        this.form.markAsPristine();
        if (
          newQuestion.id &&
          !(this.courseData && this.courseData.ref_course_id)
        ) {
          this.matSnackBar.open(
            this.translator.instant('question.message.edit')
          );
          this.updatedQuestion.emit(question);
          if (this.liveEventId) {
            this.onClose();
          }
        } else {
          if (!this.duplicateQuestionId) {
            this.matSnackBar.open(
              this.translator.instant('question.message.create')
            );
          }
          if (createAnother) {
            this.question = undefined;
            this.showForm = false;
            setTimeout(() => {
              this.ngOnInit();
              this.loading = false;
            }, 10);
            return;
          } else if (!makeDuplicate) {
            if (this.duplicateQuestionId) {
              this.openDuplicateSuccessfulDialog();
            } else if (this.flashCardId) {
              this.openConvertedQuestionDialog();
            } else {
              this.onClose();
            }
          }
        }
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        const message = error?.error?.message ?? 'question.message.editFail';
        this.matSnackBar.open(this.translator.instant(message));
      },
    });
  }

  onNavigate(questionIndex: number) {
    if (this.isImport) {
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
      this.importNavigate.emit(questionIndex);
      return;
    }
    this.showForm = false;
    if (!this.allQuestions) {
      return;
    }
    if (questionIndex < this.allQuestions.length) {
      this.question = this.allQuestions[questionIndex];
      if (this.parentQuizId) {
        if (this.courseId) {
          this.router.navigate(['/cms/edit/question', this.question.id], {
            queryParams: { course: this.courseId, quiz: this.parentQuizId },
          });
        } else if (this.bookId) {
          this.router.navigate(['/cms/edit/question', this.question.id], {
            queryParams: { book: this.bookId, quiz: this.parentQuizId },
          });
        }
      } else {
        if (this.courseId) {
          this.router.navigate(['/cms/edit/question', this.question.id], {
            queryParams: { course: this.courseId, exam: this.parentExamId },
          });
        } else if (this.bookId) {
          this.router.navigate(['/cms/edit/question', this.question.id], {
            queryParams: { book: this.bookId, exam: this.parentExamId },
          });
        } else if (this.questionIdFromContent) {
          this.router.navigate(['/cms/edit/question', this.question.id], {
            queryParams: { contentQuestionId: this.question.id },
          });
        }
      }
      generatingQuestionForm(
        this.question,
        this.form,
        this.radioRightAnswerControl,
        this.radioValueChanges,
        this.typeValueChanges
      );
    }
  }

  getTypeVal(index: number) {
    this.isPollQuestion = index === 1;
    this.setQuestionEnums();
    this.setTemplates();
    this.setTemplateType(this.isPollQuestion);
    if (this.templateType.length > 0) {
      this.selectTemplate(this.templateType[0], 0);
    }
    this.form.patchValue({ display_in_order: this.isPollQuestion });
    this.form.get('type').markAsDirty();
  }

  selectTemplate(template: any, index: number) {
    if (template.isDisabled) {
      return;
    }
    this.isPollQuestion = template.isPoll;
    this.selectedTemplateIndex = index;
    this.form.get('type').patchValue(template.type);
    if (
      this.isPollQuestion &&
      this.form.get('type').value === QuestionType.FREETEXT
    ) {
      this.form.get('max_answer_submissions').setValue(1);
    }
    this.form.get('type').markAsDirty();
  }

  setSelectedTemplateIndex() {
    if (this.templateType.length > 0 && this.form) {
      const type = this.form.get('type').value;
      const index = this.templateType.findIndex(
        (t) => t.type === type && t.isPoll === this.isPollQuestion
      );
      if (index > -1) {
        this.selectedTemplateIndex = index;
      }
    }
  }

  unlinkQuestion() {
    this.matDialog
      .open(CancelConfirmationComponent, {
        autoFocus: false,
        data: {
          messages: [
            this.translator.instant('question.form.unlinkSaveDialog.message1'),
            this.translator.instant('question.form.unlinkSaveDialog.message2'),
          ],
          buttonLabels: {
            confirm: this.translator.instant(
              'question.form.unlinkSaveDialog.copyQuestion'
            ),
            cancel: this.translator.instant('common.button.abort'),
          },
        },
      })
      .afterClosed()
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.onSubmit();
        }
      });
  }

  redirectBasedOnParent(segment, param, id) {
    if (this.parentQuizId) {
      this.router.navigate([
        `/cms/${segment}/edit/${id}`,
        { [param]: id, quiz: this.parentQuizId },
      ]);
    } else if (this.parentExamId) {
      this.router.navigate([`/cms/exams/${this.parentExamId}`], {
        queryParams: { courseId: id },
      });
    } else if (this.liveEventId) {
      this.router.navigate([`/cms/live-events/${this.liveEventId}`], {
        queryParams: { courseId: id },
      });
    } else {
      this.router.navigate([
        `/cms/${segment}/edit/${id}`,
        { [param]: id, exam: this.parentExamId },
      ]);
    }
    return;
  }

  onClose(removeQuestionFromList = false) {
    if (this.questionId) {
      this.closeSideMenu.emit(removeQuestionFromList);
      return;
    }
    if (this.courseId) {
      this.redirectBasedOnParent('courses', 'course', this.courseId);
    } else if (this.bookId) {
      this.redirectBasedOnParent('books', 'book', this.bookId);
    } else if (this.liveEventId) {
      this.router.navigate(['/cms/live-events', this.liveEventId]);
    } else if (
      this.flashCardId ||
      this.duplicateQuestionId ||
      this.liveEventId
    ) {
      this.location.back();
    } else if (this.parentQuizId) {
      this.router.navigateByUrl(`/cms/content/${this.parentQuizId}/questions`);
    } else if (this.commentId) {
      this.router.navigate(['/cms/content/comments']);
    } else if (this.parentExamId) {
      if (this.route.snapshot.queryParams?.from === 'play') {
        this.router.navigateByUrl(`/cms/exams/${this.parentExamId}`);
      } else {
        this.router.navigateByUrl(`/cms/exam/${this.parentExamId}/questions`);
      }
    } else {
      const query = { showPollQuestion: true };
      this.router.navigate(['/cms/content/questions'], {
        queryParams: this.isPollQuestion ? query : {},
      });
    }
  }

  explanationSuggestion() {
    if (
      !this.questionTypeEnum ||
      !this.form?.value ||
      this.form.value.type === null ||
      this.form.value.type === undefined
    ) {
      return '';
    }
    if (this.form.value.type === this.questionTypeEnum.TRUE_FALSE) {
      return this.getTrueFalseExplanation;
    } else if (this.form.value.type === this.questionTypeEnum.MULTIPLE_CHOICE) {
      return this.getMultiChoiceExplanation;
    } else if (this.form.value.type === this.questionTypeEnum.SINGLE_CHOICE) {
      return this.getSingleChoiceExplanation;
    }
    return '';
  }

  editImagePreUpload(img: File): Promise<File> {
    const matchArr = img.name.match(/^(.*)\.(?:.+)$/);
    return new Promise<File>((resolve) => {
      const dialogRef = this.matDialog.open(ImageEditDialogComponent, {
        panelClass: 'edit-image-dialog-panel',
        autoFocus: false,
        data: {
          file: img,
          name: matchArr ? matchArr[1] : img.name,
        },
      });
      dialogRef.afterClosed().subscribe((img) => {
        resolve(img);
      });
    });
  }

  useTemplate(template: any): void {
    this.showForm = false;
    // Reset answer form array
    const media = this.form.get('media') as UntypedFormGroup;
    media.reset();
    const formArrayAnswers = this.form.get('answers') as UntypedFormArray;
    formArrayAnswers.clear();
    for (
      let eachAnswerControl = 0;
      eachAnswerControl < template.answers.length;
      eachAnswerControl++
    ) {
      const answerFormGroup = new UntypedFormGroup({
        is_right: new UntypedFormControl(false),
        text: new UntypedFormControl(),
        id: new UntypedFormControl(),
      });
      (this.form.get('answers') as UntypedFormArray).push(answerFormGroup);
    }
    this.form.patchValue(template);
    this.setSelectedTemplateIndex();
    template.answers.forEach(
      (answer, index) =>
        (this.form.value.answers[index].is_right = answer.is_right)
    );
    if (template.type === QuestionType.FREETEXT) {
      if (template.answers?.length) {
        this.form.get('sample_answer').setValue(template.answers[0].text);
      }
    }
    window.setTimeout(() => {
      this.showForm = true;
      this.form.markAsDirty();
    }, 0);
  }

  openDialog() {
    const message = this.isPollQuestion
      ? 'question.form.fixAnswerTooltip.poll'
      : 'question.form.fixAnswerTooltip.quiz';
    this.matDialog.open<ShowInfoDialogComponent, ShowInfoItem>(
      ShowInfoDialogComponent,
      {
        data: {
          infoData: this.translator.instant(message),
        },
        width: INFO_DIALOG_WIDTH,
        height: 'auto',
        panelClass: 'tooltip-modal',
      }
    );
  }

  onSelectSingleChoiceAnswer(index: number) {
    const radioControls = (this.form.get('answers') as UntypedFormArray)
      .controls;
    radioControls.forEach((control, i) => {
      control.get('is_right').setValue(index === i);
    });
    this.suggestion = this.explanationSuggestion();
    this.form.markAsDirty();
  }
}
